import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe } from "lucide-react";

import type { Branding } from "@lagoon/config/branding";
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Mono,
  PageHeader,
  Text,
} from "@lagoon/ui";

import { api, type Site } from "../api.js";
import { Failure } from "../components/feedback.js";
import { Link } from "../router.js";
import { OrgOverview } from "../components/org-overview.js";
import { isEmailUnverified, VerifyEmailNotice } from "../components/verify-email.js";
import { canSeeSites, type Org } from "../orgs.js";

/**
 * The sites in the organisation the switcher is pointing at.
 *
 * Scoped rather than spanning: `GET /api/sites?orgId=` returns one org's sites,
 * and the query key carries the id so switching organisations refetches instead
 * of showing the previous one's list under the new one's name.
 *
 * A `billing` seat sees no sites at all, from here and from the MCP surface
 * alike. That is enforced by the platform, which answers the list route with a
 * 404 for a billing member exactly as it does for a stranger. The branch below
 * exists so the screen explains itself instead of painting a red error box over
 * a working, correct refusal.
 */
export function Sites(props: {
  org: Org;
  navigate: (to: string) => void;
  sitesDomain: string;
  branding: Branding;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const orgId = props.org.id;
  const maySee = canSeeSites(props.org.role);

  const sites = useQuery({
    queryKey: ["sites", orgId],
    queryFn: async () => (await api.listSites(orgId)).sites,
    enabled: maySee,
  });

  const columns = useMemo(
    () => siteColumns(props.sitesDomain, props.navigate),
    [props.sitesDomain, props.navigate],
  );

  if (!maySee) {
    return (
      <div className="lg:flex lg:flex-col lg:gap-7">
        <PageHeader eyebrow={props.org.name} title="Sites" />
        <Card interactive={false}>
          <CardHeader>
            <CardTitle>Nothing here, by design</CardTitle>
            <CardDescription>
              You hold the billing seat in {props.org.name}. It exists so invoices and the
              subscription reach somebody who is not publishing the documents, so it carries no
              access to sites or their contents. Ask an owner or admin for a member seat if you need
              one.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="lg:flex lg:flex-col lg:gap-7">
      <PageHeader
        eyebrow={props.org.name}
        title="Sites"
        actions={
          <Button
            onClick={() => {
              setCreating(true);
            }}
            disabled={creating}
          >
            New site
          </Button>
        }
      />

      {/*
        Plan, trial and quota, for every role that can see a site (LGN-170).
        Here rather than on the billing screen because that screen is `owner`
        and `billing` only, and this is the screen everybody lands on.
      */}
      <OrgOverview orgId={orgId} />

      {creating ? (
        <CreateSite
          orgId={orgId}
          sitesDomain={props.sitesDomain}
          supportEmail={props.branding.supportEmail}
          onCancel={() => {
            setCreating(false);
          }}
          onCreated={async (site) => {
            setCreating(false);
            await queryClient.invalidateQueries({ queryKey: ["sites", orgId] });
            await queryClient.invalidateQueries({ queryKey: ["entitlements", orgId] });
            props.navigate(`/sites/${site.id}`);
          }}
        />
      ) : null}

      {sites.isError ? (
        <Failure error={sites.error} fallback="Could not load sites." />
      ) : (
        <DataTable
          caption="Sites in this organisation"
          columns={columns}
          data={sites.data ?? []}
          loading={sites.isPending}
          empty={
            <EmptyState
              icon={<Globe />}
              title="No sites yet"
              description="A site is a private static website with its own subdomain and its own list of people who may see it."
              action={
                <div className="lg:flex lg:flex-col lg:items-center lg:gap-4">
                  <Button
                    onClick={() => {
                      setCreating(true);
                    }}
                  >
                    Create your first site
                  </Button>
                  <Text size="caption" tone="subtle">
                    New to this?{" "}
                    <Link to="/help" navigate={props.navigate}>
                      How {props.branding.productName} works
                    </Link>{" "}
                    covers what goes in the ZIP and how people get access.
                  </Text>
                </div>
              }
            />
          }
        />
      )}
    </div>
  );
}

/**
 * The columns.
 *
 * Built outside the component so the definition is not rebuilt on every render,
 * and taking the two things it closes over as arguments rather than reaching
 * for them. The name is the link: a whole clickable row cannot be a real link
 * inside a table without nesting an anchor per cell, and a row a keyboard
 * cannot reach is worse than one extra click target.
 */
function siteColumns(sitesDomain: string, navigate: (to: string) => void): DataTableColumn<Site>[] {
  return [
    {
      accessorKey: "name",
      header: "Site",
      cell: (info) => {
        const site = info.row.original;
        return (
          <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
            <Link
              to={`/sites/${site.id}`}
              navigate={navigate}
              className="lg:font-medium lg:text-strong"
            >
              {site.name}
            </Link>
            {site.suspended ? <Badge tone="danger">suspended</Badge> : null}
            {site.publicMode ? <Badge tone="quiet">public</Badge> : null}
          </span>
        );
      },
    },
    {
      accessorKey: "slug",
      header: "Address",
      cell: (info) => (
        <Mono className="lg:text-caption lg:text-muted-foreground">
          {info.row.original.slug}.{sitesDomain}
        </Mono>
      ),
    },
    {
      id: "state",
      header: "State",
      enableSorting: false,
      cell: (info) =>
        info.row.original.publishedDeployId === null ? (
          <Badge tone="neutral">not published</Badge>
        ) : (
          <Badge tone="success">live</Badge>
        ),
    },
  ];
}

/**
 * Creating a site.
 *
 * The slug becomes a hostname, so it is checked against the server's rules —
 * including the reserved-name list — before the create is attempted. Those
 * rules are load-bearing here: tenants and platform surfaces share one
 * namespace, so `app` or `admin` must never be claimable.
 */
function CreateSite(props: {
  orgId: string;
  sitesDomain: string;
  supportEmail: string;
  onCancel: () => void;
  onCreated: (site: Site) => void | Promise<void>;
}): React.ReactElement {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [touchedSlug, setTouchedSlug] = useState(false);

  const effectiveSlug = touchedSlug ? slug : suggestSlug(name);

  const availability = useQuery({
    queryKey: ["slug", effectiveSlug],
    queryFn: () => api.checkSlug(effectiveSlug),
    enabled: effectiveSlug.length >= 3,
  });

  /**
   * How many sites this organisation has left.
   *
   * A hint, and the form treats it as one: the count and the insert only agree
   * inside `createSite`'s transaction, so two people filling this in at the
   * same moment are settled there rather than here. What it buys is telling
   * somebody the answer before they have typed a name instead of after.
   */
  const quota = useQuery({
    queryKey: ["entitlements", props.orgId],
    queryFn: () => api.getEntitlements(props.orgId),
  });

  const create = useMutation({
    mutationFn: () => api.createSite(effectiveSlug, name.trim(), props.orgId),
    onSuccess: async (result) => {
      await props.onCreated(result.site);
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  const reason = availability.data?.available === false ? availability.data.reason : null;

  return (
    <Card as="form" interactive={false} onSubmit={submit}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <div className="lg:flex lg:flex-wrap lg:items-baseline lg:justify-between lg:gap-3">
          <CardTitle>New site</CardTitle>
          {quota.data === undefined ? null : (
            <Text as="span" size="caption" tone="subtle">
              {quota.data.sites.used} of {quota.data.sites.quota} sites used
              {quota.data.sites.remaining === 0 ? ", none left on this plan" : ""}
            </Text>
          )}
        </div>

        {/*
          An unverified address gets its own notice rather than the message,
          because the remedy is a thing to do and not a thing to read.
        */}
        {create.isError ? (
          isEmailUnverified(create.error) ? (
            <VerifyEmailNotice supportEmail={props.supportEmail} />
          ) : (
            <Failure error={create.error} fallback="Could not create the site." />
          )
        ) : null}

        <FormField label="Name" id="site-name" required>
          <Input
            value={name}
            autoFocus
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Q3 Board Pack"
            required
          />
        </FormField>

        <FormField
          label="Address"
          id="site-slug"
          required
          hint={
            effectiveSlug.length >= 3 ? (
              <Mono>
                {effectiveSlug}.{props.sitesDomain}
              </Mono>
            ) : (
              "Lowercase letters, digits and hyphens."
            )
          }
          {...(reason === null ? {} : { error: reason })}
        >
          <Input
            value={effectiveSlug}
            onChange={(event) => {
              setTouchedSlug(true);
              setSlug(event.target.value.toLowerCase());
            }}
            placeholder="q3-board-pack"
            required
          />
        </FormField>

        <div className="lg:flex lg:flex-wrap lg:gap-3">
          <Button
            type="submit"
            disabled={
              create.isPending ||
              name.trim().length === 0 ||
              effectiveSlug.length < 3 ||
              availability.data?.available === false
            }
          >
            {create.isPending ? "Creating…" : "Create site"}
          </Button>
          <Button variant="ghost" type="button" onClick={props.onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** A hostname-safe suggestion from the display name. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}
